import * as ents from '@attain-apps/entities'
import type * as chartJs from 'chart.js'

import type * as color from '../../../../../color'
import * as colors from '../../../colors'

/**
 * Converts chart data into Chart.js compatible format for bar and stacked bar charts.
 *
 * @example
 * // Simple bar chart with multiple groups
 * const simpleData = {
 *   values: [
 *     { name: 'Sales', points: [{ x: 'Q1', y: 100 }, { x: 'Q2', y: 150 }] },
 *     { name: 'Marketing', points: [{ x: 'Q1', y: 80 }, { x: 'Q2', y: 120 }] }
 *   ]
 * }
 * // Returns: {
 * //   datasets: [
 * //     { label: 'Sales', data: [100, 150], backgroundColor: '#color1' },
 * //     { label: 'Marketing', data: [80, 120], backgroundColor: '#color2' }
 * //   ],
 * //   labels: ['Q1', 'Q2']
 * // }
 * @example
 * // Stacked bar chart - segments sorted by value at each x-position
 * const stackedData = {
 *   values: [
 *     { name: 'Product A', points: [{ x: 'Jan', y: 50 }, { x: 'Feb', y: 20 }] },
 *     { name: 'Product B', points: [{ x: 'Jan', y: 30 }, { x: 'Feb', y: 80 }] }
 *   ]
 * }
 * // For Jan: Product A (50) stacked above Product B (30)
 * // For Feb: Product B (80) stacked above Product A (20)
 * // Returns position-based datasets where each position gets different colors
 */
export function getData(
  data: ents.core.display.chart.data.Of<
    ents.core.display.chart.Type.Bar | ents.core.display.chart.Type.StackedBar
  >,
  type: ents.core.display.chart.Type,
): chartJs.ChartData<'bar'> {
  // TODO: support multiple charts in one widget
  const { values } = data

  // Early return for empty data to avoid unnecessary processing
  if (!values?.length) {
    return { datasets: [], labels: [] }
  }

  const isStacked = type === ents.core.display.chart.Type.StackedBar

  const palette = colors.createPalette(ents.core.display.chart.Type.Bar)

  // Extract unique x-axis labels from all data points
  const xLabels = Array.from(
    new Set(
      values.flatMap(value => value.points?.map(point => point.x ?? 0) ?? []),
    ),
  )

  // Handle simple bar chart case
  if (!isStacked) {
    return createSimpleBarChart(values, xLabels, palette)
  }

  // Handle stacked bar chart with position-based sorting
  return createStackedBarChart(values, xLabels, palette)
}

/**
 * Creates a simple (non-stacked) bar chart configuration.
 *
 * Think of it like a regular bar chart where you have different colored bars
 * next to each other for comparison (e.g., Sales vs Marketing by quarter).
 *
 * @example
 * // Input: Two data groups (Sales and Marketing) across two quarters
 * const values = [
 *   {
 *     name: 'Sales',
 *     color: 'blue',
 *     points: [{ x: 'Q1', y: 1000 }, { x: 'Q2', y: 1200 }]
 *   },
 *   {
 *     name: 'Marketing',
 *     color: 'red',
 *     points: [{ x: 'Q1', y: 800 }, { x: 'Q2', y: 900 }]
 *   }
 * ]
 * const xLabels = ['Q1', 'Q2']
 *
 * // Output: Chart.js format with separate datasets for each group
 * // Result will show:
 * // Q1: Blue bar (1000) next to Red bar (800)
 * // Q2: Blue bar (1200) next to Red bar (900)
 * {
 *   datasets: [
 *     {
 *       label: 'Sales',
 *       data: [1000, 1200],
 *       backgroundColor: 'blue'
 *     },
 *     {
 *       label: 'Marketing',
 *       data: [800, 900],
 *       backgroundColor: 'red'
 *     }
 *   ],
 *   labels: ['Q1', 'Q2']
 * }
 */
function createSimpleBarChart(
  values: readonly ents.core.display.chart.data.Point.Group[] | undefined,
  xLabels: (string | number)[],
  palette: color.Palette,
): chartJs.ChartData<'bar'> {
  const datasets: chartJs.ChartDataset<'bar'>[] =
    values?.map(({ color: pointGroupColor, name, points }, index) => {
      // Create a map for point lookup
      const pointMap = new Map(
        points?.map(point => [point.x ?? 0, point.y ?? 0]) ?? [],
      )

      return {
        backgroundColor:
          pointGroupColor ??
          palette.getOrAssignColorFor(name ?? index.toString()),
        barPercentage: 0.7,
        // Map x-labels to corresponding y-values
        data: xLabels.map(x => pointMap.get(x) ?? 0),
        label: name,
      }
    }) ?? []

  return { datasets, labels: xLabels }
}

/**
 * Creates a stacked bar chart where data segments are stacked on top of each other
 * and automatically sorted by value at each x-position.
 *
 * Unlike regular stacked charts, this creates a unique visual where the largest
 * values appear at the bottom and smaller values stack on top. Each segment
 * can have its own color, creating a dynamic, sorted stack.
 *
 * @example
 * // Input: Three product sales across two months
 * const values = [
 *   {
 *     name: 'Product A',
 *     color: 'red',
 *     points: [{ x: 'Jan', y: 100 }, { x: 'Feb', y: 30 }]
 *   },
 *   {
 *     name: 'Product B',
 *     color: 'blue',
 *     points: [{ x: 'Jan', y: 50 }, { x: 'Feb', y: 90 }]
 *   },
 *   {
 *     name: 'Product C',
 *     color: 'green',
 *     points: [{ x: 'Jan', y: 75 }, { x: 'Feb', y: 60 }]
 *   }
 * ]
 * const xLabels = ['Jan', 'Feb']
 * // Visual result:
 * // January stack (bottom to top): A(100-red), C(75-green), B(50-blue)
 * // February stack (bottom to top): B(90-blue), C(60-green), A(30-red)
 * //
 * // Notice how the order changes based on values at each month!
 * // Chart.js output: Position-based datasets (not groups-based)
 * {
 *   datasets: [
 *     { // Bottom position
 *       data: [100, 90], // A's value in Jan, B's value in Feb
 *       backgroundColor: ['red', 'blue'] // A's color, B's color
 *     },
 *     { // Middle position
 *       data: [75, 60], // C's value in both months
 *       backgroundColor: ['green', 'green'] // C's color both times
 *     },
 *     { // Top position
 *       data: [50, 30], // B's value in Jan, A's value in Feb
 *       backgroundColor: ['blue', 'red'] // B's color, A's color
 *     }
 *   ],
 *   labels: ['Jan', 'Feb']
 * }
 */
function createStackedBarChart(
  values: readonly ents.core.display.chart.data.Point.Group[] | undefined,
  xLabels: (string | number)[],
  palette: color.Palette,
): chartJs.ChartData<'bar'> {
  // Pre-compute point group metadata to avoid repeated calculations
  const pointGroupMetadata = values?.map((value, index) => ({
    color:
      value.color ??
      palette.getOrAssignColorFor(value.name ?? `Group ${index}`),
    name: value.name ?? `Group ${index}`,
    pointMap: new Map(
      value.points?.map(point => [point.x ?? 0, point.y ?? 0]) ?? [],
    ),
  }))

  // Create empty datasets for each potential stack position
  const datasets: chartJs.ChartDataset<'bar'>[] = Array.from(
    { length: pointGroupMetadata?.length ?? 0 },
    () => ({
      backgroundColor: new Array(xLabels.length).fill(''),
      barPercentage: 0.7,
      data: new Array(xLabels.length).fill(0),
    }),
  )

  // Process each x-position independently
  xLabels.forEach((xValue, xIndex) => {
    // Collect all y-values for this x-position with their group info
    const segmentsAtX = pointGroupMetadata
      ?.map(({ name, color: _color, pointMap }) => ({
        color: _color,
        name,
        value: pointMap.get(xValue) ?? 0,
      }))
      .sort((a, b) => b.value - a.value) // Sort descending by value

    // Assign sorted segments to dataset positions
    segmentsAtX?.forEach((segment, position) => {
      if (position < datasets.length) {
        datasets[position].data[xIndex] = segment.value
        // Casting to string array because in this transformation, we will
        // always have string format for color
        ;(datasets[position].backgroundColor as string[])[xIndex] =
          segment.color
      }
    })
  })

  return {
    datasets,
    labels: xLabels,
  }
}
